/**
 * F23 — Wizard de importación masiva desde Mikrowisp.
 *
 * Flujo:
 *   1. El admin sube los CSVs por SFTP a /opt/byteisp/import/ en el VPS.
 *   2. Click "Preview" → backend lee CSVs y cuenta qué se importaría.
 *   3. Verifica conteos, warnings, errores. Si OK, click "Ejecutar".
 *   4. Backend hace el import real best-effort. UI muestra reporte final.
 */
import { useState } from "react";
import {
  Database, AlertTriangle, CheckCircle2, Loader2, Eye, Play, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ApiError } from "@/lib/api";
import { useExecuteImport, usePreviewImport, type ImportReport } from "./api";

export function ImportacionPage() {
  const [directorio, setDirectorio] = useState("/opt/byteisp/import");
  const [report, setReport] = useState<ImportReport | null>(null);
  const [reportTipo, setReportTipo] = useState<"preview" | "ejecucion" | null>(null);
  const [confirmExecute, setConfirmExecute] = useState(false);

  const previewMut = usePreviewImport();
  const executeMut = useExecuteImport();

  async function handlePreview() {
    setConfirmExecute(false);
    try {
      const r = await previewMut.mutateAsync(directorio);
      setReport(r);
      setReportTipo("preview");
      toast.success("Preview listo", {
        description: `${r.clientes.a_crear} clientes, ${r.contratos.a_crear} contratos a crear`,
      });
    } catch (e) {
      toast.error("Error al hacer preview", {
        description: (e as ApiError).message,
      });
    }
  }

  async function handleExecute() {
    if (!confirmExecute) {
      setConfirmExecute(true);
      return;
    }
    try {
      const r = await executeMut.mutateAsync(directorio);
      setReport(r);
      setReportTipo("ejecucion");
      setConfirmExecute(false);
      toast.success("Import completado", {
        description: `${r.clientes.creados} clientes, ${r.contratos.creados} contratos creados`,
      });
    } catch (e) {
      toast.error("Error al ejecutar", {
        description: (e as ApiError).message,
      });
    }
  }

  return (
    <div className="flex flex-col gap-4 max-w-5xl">
      <div>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Database size={16} className="text-primary" />
          Importación masiva — Mikrowisp
        </h1>
        <p className="text-xs text-muted-foreground">
          Trae clientes, planes y contratos desde Mikrowisp v6 al sistema ByteISP.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Antes de ejecutar</CardTitle>
          <CardDescription>Pre-condiciones para que el import funcione:</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
            <li>
              Subir los 6 CSVs por SFTP al directorio del VPS:
              <code className="ml-1 rounded bg-muted px-1 font-mono">
                clientes, planes, servicios, servicios_tecnico, facturas_pendientes, avisos_facturacion
              </code>
            </li>
            <li>
              Existir un router llamado{" "}
              <code className="rounded bg-muted px-1 font-mono">Router - Borde</code> al cual se asignarán todos los contratos.
            </li>
            <li>
              Tener creadas las redes nuevas con nombres que matcheen los sites de Mikrowisp
              (ej. <code className="rounded bg-muted px-1 font-mono">Red - Honey</code>).
              El importer las matchea por nombre normalizado (sin "Red -", sin "Site", sin "Up/Down").
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Configuración</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Directorio en el VPS con los CSVs</Label>
            <Input
              value={directorio}
              onChange={(e) => setDirectorio(e.target.value)}
              placeholder="/opt/byteisp/import"
              className="font-mono"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Path absoluto. Default es <code>/opt/byteisp/import</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePreview}
              disabled={previewMut.isPending}
              variant="outline"
            >
              {previewMut.isPending ? (
                <><Loader2 size={12} className="animate-spin" /> Analizando…</>
              ) : (
                <><Eye size={12} /> 1. Preview (dry-run)</>
              )}
            </Button>
            <Button
              onClick={handleExecute}
              disabled={
                executeMut.isPending ||
                !report ||
                reportTipo === "ejecucion"
              }
              className={
                confirmExecute
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }
            >
              {executeMut.isPending ? (
                <><Loader2 size={12} className="animate-spin" /> Ejecutando…</>
              ) : confirmExecute ? (
                <><AlertTriangle size={12} /> Confirmar ejecución</>
              ) : (
                <><Play size={12} /> 2. Ejecutar import real</>
              )}
            </Button>
            {confirmExecute && (
              <Button variant="ghost" size="sm" onClick={() => setConfirmExecute(false)}>
                Cancelar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {report && <ImportReportView report={report} tipo={reportTipo!} />}
    </div>
  );
}

// ─── Vista del reporte ──────────────────────────────────────────────────
function ImportReportView({
  report, tipo,
}: { report: ImportReport; tipo: "preview" | "ejecucion" }) {
  const isPreview = tipo === "preview";
  return (
    <Card className={isPreview ? "" : "border-emerald-500/50"}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">
            {isPreview ? "📋 Preview" : "✅ Ejecución completada"}
          </CardTitle>
          {!isPreview && (
            <Badge variant="success" className="text-[10px]">
              persistido
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* CSVs leídos */}
        <Section title="CSVs leídos">
          <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
            {Object.entries(report.csvs_leidos).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded border bg-muted/20 px-2 py-1">
                <span className="font-mono text-[10px]">{k}.csv</span>
                <strong>{v}</strong>
              </div>
            ))}
          </div>
        </Section>

        {/* Conteos */}
        <Section title="Conteos">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Stat label="Clientes a crear" value={report.clientes.a_crear} good={report.clientes.creados} ok={isPreview} />
            <Stat label="Planes a crear" value={report.planes.a_crear} good={report.planes.creados} ok={isPreview} />
            <Stat label="Contratos a crear" value={report.contratos.a_crear} good={report.contratos.creados} ok={isPreview} />
            <Stat label="Facturas pendientes" value={report.facturas_pendientes.a_crear} good={report.facturas_pendientes.creadas} ok={isPreview} />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground md:grid-cols-3">
            <span>Clientes ya existentes: <strong>{report.clientes.existentes}</strong></span>
            <span>Clientes descartados (no activos): <strong>{report.clientes.descartados_estado}</strong></span>
            <span>Contratos sin red match: <strong>{report.contratos.sin_red_match}</strong></span>
          </div>
        </Section>

        {/* Warnings */}
        {report.warnings.length > 0 && (
          <Section title={`⚠️ Warnings (${report.warnings.length})`}>
            <ul className="space-y-1 text-[11px] text-amber-700 dark:text-amber-400">
              {report.warnings.slice(0, 20).map((w, i) => (
                <li key={i}>• {w}</li>
              ))}
              {report.warnings.length > 20 && (
                <li className="italic">… +{report.warnings.length - 20} más</li>
              )}
            </ul>
          </Section>
        )}

        {/* Errores */}
        {report.errores_total > 0 && (
          <Section title={`❌ Errores (${report.errores_total})`}>
            <div className="rounded border border-destructive/30 bg-destructive/5">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-2 py-1 text-left">Categoría</th>
                    <th className="px-2 py-1 text-left">Ref</th>
                    <th className="px-2 py-1 text-left">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {report.errores.map((e, i) => (
                    <tr key={i} className="border-t border-destructive/20">
                      <td className="px-2 py-1 font-mono">{e.categoria}</td>
                      <td className="px-2 py-1 font-mono text-muted-foreground">{e.ref}</td>
                      <td className="px-2 py-1">{e.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.errores_total > report.errores.length && (
                <div className="border-t border-destructive/20 px-2 py-1 text-[11px] italic text-muted-foreground">
                  Mostrando {report.errores.length} de {report.errores_total} errores. Revisa los logs del servidor para el detalle completo.
                </div>
              )}
            </div>
          </Section>
        )}

        {report.errores_total === 0 && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-50 p-2 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
            <CheckCircle2 size={14} />
            Sin errores en {isPreview ? "el preview" : "la ejecución"}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, good, ok }: { label: string; value: number; good: number; ok: boolean }) {
  return (
    <div className="rounded-md border bg-muted/20 p-2">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{ok ? value : `${good}/${value}`}</div>
    </div>
  );
}
